import type { Text } from 'pixi.js';

export interface TextLayoutDebugEntry {
  id: string;
  visible: boolean;
  textLength: number;
  width: number;
  height: number;
  maxWidth: number;
  maxHeight: number;
  fontSize: number;
  lineHeight: number | null;
  overflowX: boolean;
  overflowY: boolean;
}

export interface TextLayoutDebugState {
  scene: string;
  entries: TextLayoutDebugEntry[];
}

export interface TextFitOptions {
  maxWidth: number;
  maxHeight?: number;
  baseFontSize?: number;
  minFontSize?: number;
  baseLineHeight?: number;
  minLineHeight?: number;
  wordWrap?: boolean;
  breakWords?: boolean;
}

const DEFAULT_MIN_FONT_SIZE = 13;
const OVERFLOW_EPSILON = 0.75;

export function fitTextToBounds(text: Text, options: TextFitOptions): void {
  const maxWidth = normalizePositive(options.maxWidth, 1);
  const maxHeight =
    options.maxHeight === undefined ? null : normalizePositive(options.maxHeight, 1);
  const baseFontSize = normalizePositive(
    options.baseFontSize,
    getNumericStyleValue(text.style.fontSize, 22)
  );
  const minFontSize = Math.min(
    baseFontSize,
    normalizePositive(options.minFontSize, DEFAULT_MIN_FONT_SIZE)
  );
  const baseLineHeight = normalizePositive(
    options.baseLineHeight,
    getNumericStyleValue(text.style.lineHeight, Math.round(baseFontSize * 1.28))
  );
  const minLineHeight = Math.min(
    baseLineHeight,
    normalizePositive(options.minLineHeight, Math.round(minFontSize * 1.22))
  );

  text.style.fontSize = baseFontSize;
  text.style.lineHeight = baseLineHeight;
  text.style.wordWrap = options.wordWrap ?? true;
  text.style.breakWords = options.breakWords ?? true;
  if (text.style.wordWrap) {
    text.style.wordWrapWidth = maxWidth;
  }

  while (
    text.text.length > 0 &&
    isOverflowing(text, maxWidth, maxHeight) &&
    getNumericStyleValue(text.style.fontSize, baseFontSize) > minFontSize
  ) {
    const nextFontSize = getNumericStyleValue(text.style.fontSize, baseFontSize) - 1;
    const lineHeightRatio = nextFontSize / baseFontSize;
    text.style.fontSize = nextFontSize;
    text.style.lineHeight = Math.max(minLineHeight, Math.round(baseLineHeight * lineHeightRatio));
  }
}

export function createTextLayoutDebugEntry(
  id: string,
  text: Text,
  maxWidth: number,
  maxHeight: number
): TextLayoutDebugEntry {
  const normalizedMaxWidth = normalizePositive(maxWidth, 1);
  const normalizedMaxHeight = normalizePositive(maxHeight, 1);
  const width = text.visible ? text.width : 0;
  const height = text.visible ? text.height : 0;

  return {
    id,
    visible: text.visible,
    textLength: text.text.length,
    width,
    height,
    maxWidth: normalizedMaxWidth,
    maxHeight: normalizedMaxHeight,
    fontSize: getNumericStyleValue(text.style.fontSize, 0),
    lineHeight: getNullableNumericStyleValue(text.style.lineHeight),
    overflowX: text.visible && width > normalizedMaxWidth + OVERFLOW_EPSILON,
    overflowY: text.visible && height > normalizedMaxHeight + OVERFLOW_EPSILON
  };
}

function isOverflowing(text: Text, maxWidth: number, maxHeight: number | null): boolean {
  return (
    text.width > maxWidth + OVERFLOW_EPSILON ||
    (maxHeight !== null && text.height > maxHeight + OVERFLOW_EPSILON)
  );
}

function normalizePositive(value: number | undefined, fallback: number): number {
  if (value === undefined || !Number.isFinite(value)) {
    return fallback;
  }

  return Math.max(1, value);
}

function getNumericStyleValue(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getNullableNumericStyleValue(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
